// src/pages/ObOverview.js

import React, { useState, useMemo, useEffect } from 'react';
import { Nav } from '../../components/nav';
import { useAuth } from '../../navigation/AuthProvider';
import "../css/Payroll.css";
import axios from 'axios';
import { BACKEND_URL } from '../../constants';
import { Toast } from '../../components/ToastPopup';
import { calculatePayForEmployee } from '../../utils/calculatePayForEmployee';

export const ObOverview = () => {
  const {
    employees,
    obs,
    timeSchemas,
    company,
    user,
    pays,
    vacationPay,
    overtimeRules,
    obRules,
    absenceRules,
  } = useAuth();

  const [toastMessage, setToastMessage] = useState("");
  const [toastType, setToastType] = useState("success");
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedSchema, setSelectedSchema] = useState("");
  const [selectedMonth, setSelectedMonth] = useState(new Date().getMonth() + 1);
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
  const [loading, setLoading] = useState(false);
  const [compactMode, setCompactMode] = useState(false);

  const [shifts, setShifts] = useState([]);

  const monthsInSwedish = [
    'Januari','Februari','Mars','April','Maj','Juni',
    'Juli','Augusti','September','Oktober','November','December'
  ];

  // 🔥 SAFE helper (ADDED)
  const getEmployeeId = (e) => {
    if (!e?.employeeId) return null;
    return typeof e.employeeId === 'object'
      ? e.employeeId?._id
      : e.employeeId;
  };

  const isShiftCompleteForPayroll = (s) => {
    const empHasId = Array.isArray(s?.employees) &&
      s.employees.some(e => {
        const id = getEmployeeId(e); // ✅ FIX
        return !!id;
      });

    return !!(
      s &&
      s.status === "complete" &&
      s.date &&
      typeof s.startTime === "string" && s.startTime.includes(":") &&
      typeof s.endTime === "string" && s.endTime.includes(":") &&
      empHasId
    );
  };

  useEffect(() => {
    const fetchShifts = async () => {
      setLoading(true);
      try {
        const { data } = await axios.get(
          `${BACKEND_URL}/get-shifts-for-month`,
          {
            params: {
              userId: user._id,
              companyId: company._id,
              selectedMonth,
              selectedYear
            }
          }
        );
        setShifts(Array.isArray(data.shifts) ? data.shifts : []);
      } catch (err) {
        console.error("Nätverksfel eller serverfel:", err);
        const serverMsg = err.response?.data?.message;
        setToastType("error");
        setToastMessage(serverMsg || "Kunde inte hämta skift – se konsolen");
      } finally {
        setLoading(false);
      }
    };

    fetchShifts();
  }, [company._id, selectedMonth, selectedYear, user._id, setShifts]);

  const filteredEmployees = useMemo(() =>
    employees.filter(emp => {
      const text = searchQuery.toLowerCase();
      return (
        (emp.name || "").toLowerCase().includes(text) ||
        (emp.email || "").toLowerCase().includes(text) ||
        (emp.role || "").toLowerCase().includes(text)
      );
    })
  , [employees, searchQuery]);

  const relevantShiftsForEmp = (emp) => {
    return shifts.filter(s => {
      if (!isShiftCompleteForPayroll(s)) return false;

      const d = new Date(s.date);
      if (isNaN(d)) return false;

      const sameMonth = d.getMonth() + 1 === selectedMonth;
      const sameYear  = d.getFullYear() === selectedYear;
      const sameSchema = !selectedSchema || String(s.timeSchema) === String(selectedSchema);

      const hasEmp = Array.isArray(s.employees) && s.employees.some(e => {
        const empId = getEmployeeId(e); // ✅ FIX
        if (!empId) return false;
        return String(empId) === String(emp._id);
      });

      return sameMonth && sameYear && sameSchema && hasEmp;
    });
  };

  const tableData = useMemo(() =>
    filteredEmployees.map(emp => {
      const empShifts = relevantShiftsForEmp(emp);

      const { summary } = calculatePayForEmployee({
        employee: emp,
        shifts: empShifts,
        payRates: pays || [],
        vacationPay: vacationPay || {},
        overtimeRules: overtimeRules || [],
        obRules: obRules || [],
        absenceRules: absenceRules || [],
        returnDetailed: true,
      });

      const perRuleHours = (obs || []).map(rule => {
        const ruleId = String(rule._id);

        const hoursForRule = empShifts.reduce((sum, s) => {
          const assignment = (s.employees || []).find(x => {
            const id = getEmployeeId(x); // ✅ FIX
            if (!id) return false;
            return String(id) === String(emp._id);
          });

          if (!assignment || !Array.isArray(assignment.obDetails)) return sum;

          const detail = assignment.obDetails.find(o => {
            const obId = typeof o?.obType === 'object' ? o.obType?._id : o?.obType;
            return String(obId || "") === ruleId;
          });

          return sum + Number(detail?.obHours || 0);
        }, 0);

        return hoursForRule;
      });

      const totalObHours = perRuleHours.reduce((a, b) => a + b, 0);

      return { emp, totalObHours, perRuleHours };
    })
  , [
    filteredEmployees,
    shifts,
    selectedMonth,
    selectedYear,
    selectedSchema,
    obs,
    pays, vacationPay, overtimeRules, obRules, absenceRules
  ]);

  return (
    <div className="payroll-page">
      <Nav />
      <div className="payroll-container">
        <h1 style={{ fontSize: "1.8rem", fontWeight: 700, marginBottom: 16 }}>
          OB-översikt
        </h1>

        <div className="top-inputs-container" style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 16 }}>
          <div className="search-bar-container" style={{ flex: 1 }}>
            <input
              type="text"
              placeholder="Sök efter namn, email eller roll..."
              className="search-input"
              value={searchQuery}
              onChange={e => setSearchQuery(e.target.value)}
            />
          </div>

          <select className="schema-select payroll-select" value={selectedSchema} onChange={e => setSelectedSchema(e.target.value)}>
            <option value="">-- Visa alla scheman --</option>
            {timeSchemas.map(s => (
              <option key={s._id} value={s._id}>{s.name}</option>
            ))}
          </select>

          <select className="month-select payroll-select" value={selectedMonth} onChange={e => setSelectedMonth(+e.target.value)}>
            {monthsInSwedish.map((m, i) => (
              <option key={i} value={i + 1}>{m}</option>
            ))}
          </select>

          <select className="year-select payroll-select" value={selectedYear} onChange={e => setSelectedYear(+e.target.value)}>
            {Array.from({ length: 5 }, (_, i) =>
              new Date().getFullYear() - i
            ).map(y => (
              <option key={y} value={y}>{y}</option>
            ))}
          </select>

          <label style={{ display: "inline-flex", alignItems: "center", gap: 6, marginLeft: 8, fontSize: 13, cursor: "pointer" }}>
            <input type="checkbox" checked={compactMode} onChange={e => setCompactMode(e.target.checked)} />
            Kompakt läge (endast total)
          </label>
        </div>

        {loading
          ? <div className="spinner-container"><div className="spinner" /></div>
          : (
            <div className="payroll-grid" style={{ overflowX: 'auto' }}>
              <div className="payroll-header" style={{
                display: 'flex',
                alignItems: 'center',
                gap: 8,
                minWidth: compactMode ? 520 : 320 + Math.max(0, (obs || []).length) * 120
              }}>
                <span style={{ flex: 2 }}>Namn</span>
                <span style={{ flex: 1, textAlign: 'center' }}>Total OB (h)</span>
                {!compactMode && (obs || []).map(rule => (
                  <span key={rule._id} style={{ flex: 1, textAlign: 'center' }}>
                    {rule.name}
                  </span>
                ))}
              </div>

              {tableData.map(({ emp, totalObHours, perRuleHours }) => (
                <div key={emp._id} className="payroll-row" style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 8,
                  minWidth: compactMode ? 520 : 320 + Math.max(0, (obs || []).length) * 120
                }}>
                  <span style={{ flex: 2 }}>{emp.name}</span>
                  <span style={{ flex: 1, textAlign: 'center' }}>
                    {Number(totalObHours || 0).toFixed(2)}
                  </span>
                  {!compactMode && perRuleHours.map((h, i) => (
                    <span key={`${emp._id}-${i}`} style={{ flex: 1, textAlign: 'center' }}>
                      {Number(h || 0).toFixed(2)}
                    </span>
                  ))}
                </div>
              ))}
            </div>
          )
        }

        <Toast message={toastMessage} type={toastType} onClose={() => setToastMessage("")} />
      </div>
    </div>
  );
};

export default ObOverview;